import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sprout, Beef, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProductGridSkeleton, EmptyState } from "@/components/States";
import { toNeDigits } from "@/lib/constants";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";

const Home = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/products?limit=8"), api.get("/stats")])
      .then(([p, s]) => {
        setProducts(p.data);
        setStats(s.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/market?q=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#123A2C] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-16">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight leading-snug max-w-2xl">
            {t("home.heroTitle")}
          </h1>
          <p className="mt-3 text-[rgba(255,255,255,0.72)] text-sm sm:text-base max-w-xl">
            {t("home.heroSubtitle")}
          </p>
          <form
            onSubmit={doSearch}
            className="mt-6 flex items-center gap-2 max-w-xl bg-white rounded-full p-1.5 shadow-sm"
          >
            <Search className="w-5 h-5 text-[#9A9E97] ml-3 shrink-0" />
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("home.searchPlaceholder")}
              className="flex-1 bg-transparent outline-none text-[#14251F] placeholder:text-[#9A9E97] text-sm py-2"
            />
            <button
              data-testid="hero-search-btn"
              type="submit"
              className="shrink-0 px-5 py-2.5 rounded-full bg-[#F5A623] text-[#412402] font-semibold text-sm hover:brightness-95 transition"
            >
              {t("home.search")}
            </button>
          </form>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-3 sm:gap-6 -mt-6 relative z-10">
            {[
              { v: stats.products, l: t("home.statProducts") },
              { v: stats.sellers, l: t("home.statSellers") },
              { v: stats.districts, l: t("home.statDistricts") },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`stat-card-${i}`}
                className="bg-white rounded-xl border border-[#E5E7EB] p-4 text-center shadow-sm"
              >
                <div className="text-xl sm:text-2xl font-extrabold text-[#123A2C]">
                  {toNeDigits(s.v, lang)}
                </div>
                <div className="text-xs text-[#6B7280] mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        )}
        <PromoBanner />

        {/* Categories */}
        <section className="mt-8">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#14251F] mb-4">
            {t("home.browseCategories")}
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            <button
              data-testid="home-category-produce"
              onClick={() => navigate("/market?category=produce")}
              className="text-left rounded-2xl p-5 sm:p-6 hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#DCE7E2" }}
            >
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-3">
                <Sprout className="w-6 h-6 text-[#123A2C]" />
              </div>
              <div className="font-bold text-[#14251F] text-lg">
                {t("category.produce")}
              </div>
              <div className="text-xs text-[#6B7280] mt-1">
                {t("category.produceDesc")}
              </div>
            </button>
            <button
              data-testid="home-category-livestock"
              onClick={() => navigate("/market?category=livestock")}
              className="text-left rounded-2xl p-5 sm:p-6 hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#E4DED2" }}
            >
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-3">
                <Beef className="w-6 h-6 text-[#123A2C]" />
              </div>
              <div className="font-bold text-[#14251F] text-lg">
                {t("category.livestock")}
              </div>
              <div className="text-xs text-[#6B7280] mt-1">
                {t("category.livestockDesc")}
              </div>
            </button>
          </div>
        </section>

        {/* Featured */}
        <section className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#14251F]">
              {t("home.featured")}
            </h2>
            <button
              data-testid="home-viewall"
              onClick={() => navigate("/market")}
              className="flex items-center gap-1 text-sm font-medium text-[#123A2C] hover:gap-2 transition-all"
            >
              {t("home.viewAll")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {loading ? (
            <ProductGridSkeleton />
          ) : products.length === 0 ? (
            <EmptyState message={t("empty.noListings")} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default Home;
